import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HpButton, SearchField } from '@hp/ui';
import { ErrorAlert } from '../../components/error-alert';
import { Spinner } from '../../components/ui/icons';
import { useCampaignOptions, useCatalog, useProjectOptions, type CatalogRow, type FieldOption } from './queries';
import { CampaignSubtitle } from './rules-table';

/** A chosen Pipedrive field option; the label is null until it is known (a mapped option arrives as a bare id). */
export interface Picked {
  id: string;
  label: string | null;
}

/** Search field + list of field options; `noneLabel` adds a "none" entry on top. */
function OptionList({
  q,
  onQ,
  options,
  onPick,
  onCancel,
  noneLabel,
}: {
  q: string;
  onQ: (q: string) => void;
  options: FieldOption[] | undefined;
  onPick: (option: Picked | null) => void;
  onCancel: () => void;
  noneLabel?: string;
}) {
  const { t } = useTranslation(['leads', 'common']);
  return (
    <div className="bo-sections bo-picker">
      <SearchField
        autoFocus
        value={q}
        placeholder={t('editor.optionSearch')}
        aria-label={t('editor.optionSearch')}
        onChange={(e) => onQ(e.target.value)}
      />
      <ul className="bo-picker__list">
        {noneLabel && (
          <li>
            <button type="button" className="bo-picker__option" onClick={() => onPick(null)}>
              <span className="bo-muted">{noneLabel}</span>
            </button>
          </li>
        )}
        {options === undefined && (
          <li>
            <Spinner>{t('common:actions.loading')}</Spinner>
          </li>
        )}
        {(options ?? []).map((o) => (
          <li key={o.id}>
            <button type="button" className="bo-picker__option" onClick={() => onPick({ id: o.id, label: o.label })}>
              <span>{o.label}</span>
            </button>
          </li>
        ))}
        {options && options.length === 0 && <li className="bo-muted">{t('editor.optionEmpty')}</li>}
      </ul>
      <HpButton variant="quiet" className="bo-button--sm" onClick={onCancel}>
        {t('common:actions.cancel')}
      </HpButton>
    </div>
  );
}

export function CampaignOptionList(props: { onPick: (o: Picked | null) => void; onCancel: () => void }) {
  const [q, setQ] = useState('');
  const options = useCampaignOptions(q, true);
  return <OptionList q={q} onQ={setQ} options={options.data} {...props} />;
}

export function ProjectOptionList(props: {
  onPick: (o: Picked | null) => void;
  onCancel: () => void;
  noneLabel: string;
}) {
  const [q, setQ] = useState('');
  const options = useProjectOptions(q, true);
  return <OptionList q={q} onQ={setQ} options={options.data} {...props} />;
}

/**
 * Server-side search over every Meta campaign without a rule (the catalog is Meta-only), independent of
 * the table's filters (paused, archived or deleted campaigns included: they can still bring leads). Unruled campaigns with leads come first.
 */
export function CampaignPicker({ onPick }: { onPick: (row: CatalogRow) => void }) {
  const { t } = useTranslation(['leads', 'common']);
  const [q, setQ] = useState('');
  const found = useCatalog({ scope: 'unruled', q: q.trim() || undefined, onlyRunning: false, limit: 50 });
  const shown = found.data?.rows.filter((r) => r.rule === null);
  return (
    <div className="bo-sections bo-picker">
      <SearchField
        value={q}
        placeholder={t('editor.campaignSearch')}
        aria-label={t('editor.campaignSearch')}
        onChange={(e) => setQ(e.target.value)}
      />
      <ul className="bo-picker__list">
        {found.isLoading && (
          <li>
            <Spinner>{t('common:actions.loading')}</Spinner>
          </li>
        )}
        {(shown ?? []).map((r) => (
          <li key={r.campaign.id}>
            <button type="button" className="bo-picker__option" onClick={() => onPick(r)}>
              <span>
                <CampaignLine row={r} />
              </span>
              {r.leads30d > 0 && <small className="bo-muted">{t('editor.leadsCount', { count: r.leads30d })}</small>}
            </button>
          </li>
        ))}
        {shown && shown.length === 0 && <li className="bo-muted">{t('editor.campaignEmpty')}</li>}
      </ul>
      {found.data && found.data.counts.unruled > (shown?.length ?? 0) && (
        <small className="bo-hint">
          {t('editor.campaignMore', { shown: shown?.length ?? 0, total: found.data.counts.unruled })}
        </small>
      )}
      <ErrorAlert error={found.error} />
    </div>
  );
}

export function CampaignLine({ row }: { row: CatalogRow }) {
  const c = row.campaign;
  return (
    <span className="bo-row">
      <span className="bo-strong">{c.name}</span>
      <small className="bo-muted">
        <CampaignSubtitle campaign={c} />
      </small>
    </span>
  );
}
